/**
 * cards.css — Shared "rich winery card" component styles.
 *
 * The wine / membership / event / event-details / review cards are shared by the
 * wineries that forked the same original design (halleck, hestan, valle — goose
 * folds in next). The STRUCTURE lives here once; every COLOR is read from a
 * --ws-rich-* CSS variable (defaults in variables.css :host). A variant themes
 * its cards by setting those vars in its own styles.css and overrides a rule
 * here ONLY when it structurally differs.
 *
 * Injected by init.js AFTER variables.css and BEFORE the per-variant CSS, so
 * variant vars + structural overrides win.
 *
 * The card-component rules are NOT winery-scoped — they apply via plain
 * `:host .ws-...` selectors. That's safe because only the rich-card variants
 * (goose, halleck, hestan, valle) emit these classes; Fogarty never does. All
 * four wineries share these styles and theme them with --ws-rich-* vars; a
 * winery overrides a rule only where it structurally differs (e.g. goose's flat
 * white event card resets the cover/blur in its own styles.css).
 *
 * Still family-scoped (NOT card structure — chrome/theme shared by the original
 * magenta trio): the --ws-* var block, the launcher blink, .ws-prompt-button,
 * and the generic .ws-card-cta (which base/Fogarty cards also use).
 *
 * NOTE: class names are still ws-rich-* / ws-goose-* (rename to ws-rich-* pending).
 */

/* Launcher blink — common to all wineries (the 3-pulse "Ask <bot>" rail). */
@keyframes ws-rich-blink {
  0%, 100% { transform: translateY(-50%) scale(1);    box-shadow: -4px 0 10px rgba(0,0,0,0.3); }
  50%      { transform: translateY(-50%) scale(1.04); box-shadow: -6px 0 18px rgba(184,143,55,0.55); }
}
:host #ws-launcher { animation: ws-rich-blink 1.6s ease-in-out 3s 3; }

/* ── Wine listing card (old createWineListing) ───────────────────────
   Image → "Tasting notes" button → title → price+bottle_quantity →
   FULL-WIDTH add-to-cart. SOLD OUT overlay at the top of the card.
   Equal-height carousel: stretch column + pinned footer so cards in a
   row share one height even when titles/meta wrap differently. */
:host .ws-rich-winecard {
  position: relative;
  width: 240px;
  flex: 0 0 auto;
  height: 100%;
  min-height: 100%;
  padding: 0.5rem;
  background: var(--ws-rich-card-bg);
  border-radius: 12px;
  border: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
  box-shadow: var(--ws-rich-card-shadow); 
  font-family: var(--ws-font);
}

:host .ws-rich-winecard p {
  margin: 0;
}

:host .ws-rich-soldout {
  background: var(--ws-rich-deep);
  color: var(--ws-rich-on-accent);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-align: center;
  padding: 4px 0;
  flex-shrink: 0;
}
/* Optional discount badge (compare_at_price > regular_price). Hidden when absent. */
:host .ws-rich-discount {
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
}
:host .ws-rich-discount-pct {
  display: inline-block;
  width: 47px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  background: var(--ws-rich-cta-bg);
  color: var(--ws-rich-on-accent);
  padding: 1px 3px;
  border-radius: 8px 0 6px 0;
}
:host .ws-rich-meta {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: var(--ws-rich-muted, #666);
  line-height: 1.3;
  min-height: 2.6em;
  max-height: 2.6em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
:host .ws-rich-head {
  padding: 12px 12px 0;
  flex-shrink: 0;
}
:host .ws-rich-head .ws-card-img {
  width: 100%;
  height: 170px;
  object-fit: contain;
  display: block;
}
:host .ws-rich-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px 12px;
  flex: 1 1 auto;
  min-height: 0;
}
:host .ws-rich-tasting {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 4px 10px;
  background: var(--ws-rich-card-bg);
  border: 1px solid var(--ws-rich-tasting-border);
  border-radius: 999px;
  color: var(--ws-rich-accent);
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
  line-height: 1.2;
  box-sizing: border-box;
  font-family: inherit;
}
:host .ws-rich-tasting svg {
  width: 12px;
  height: 15px;
  flex-shrink: 0;
  display: block;
}
:host .ws-rich-tasting span {
  display: inline-block;
}
:host .ws-rich-tasting:hover {
  border-color: var(--ws-rich-accent);
  color: var(--ws-rich-accent-hover);
}
:host .ws-rich-winetitle {
  font-size: 14px;
  font-weight: 700;
  color: var(--ws-rich-title);
  margin: 0;
  line-height: 1.25;
  /* min-height: 35px;
  max-height: 35px; */
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
:host .ws-rich-footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: auto;
  flex-shrink: 0;
}
:host .ws-rich-pricerow {
  display: flex;
  align-items: baseline;
  gap: 6px;
}
:host .ws-rich-pricerow .ws-card-price {
  font-size: 16px;
  font-weight: 700;
  color: var(--ws-rich-title);
  margin: 0;
}
:host .ws-rich-pricerow .ws-card-unit {
  font-size: 11px;
  color: var(--ws-rich-muted);
  margin: 0;
}
/* Full-width add-to-cart (old .add-to-cart width:100%). */
:host .ws-rich-cta {
  display: block;
  width: 100%;
  text-align: center;
  padding: 9px 12px;
  background: var(--ws-rich-cta-bg);
  color: var(--ws-rich-on-accent);
  border-radius: 4px;
  text-decoration: none;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  box-sizing: border-box;
}
:host .ws-rich-cta:hover {
  background: var(--ws-rich-cta-bg-hover);
}

/* ── Membership card ─────────────────────────────────────────────────── */
:host .ws-rich-membership {
  /* background: var(--ws-rich-deep); */
  border-radius: 8px;
  overflow: hidden;
}
:host .ws-rich-membership .ws-card-title {
  /* color: var(--ws-rich-on-accent); */
  /* text-align: center; */
  padding: 8px 10px 4px;
  margin: 0;
}
:host .ws-rich-membership .ws-card-head { background: var(--ws-rich-deep); }
:host .ws-rich-inner {
  /* background: linear-gradient(180deg, var(--ws-rich-inner-from) 0%, var(--ws-rich-card-bg) 100%);
  padding: 12px; */
  margin: 0 8px 8px;
  border-radius: 6px;
}
:host .ws-rich-bullets {
  list-style: none;
  margin: 6px 0 8px;
  padding: 0;
}
:host .ws-rich-bullets li {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  line-height: 1.35;
  margin-bottom: 4px;
  color: var(--ws-rich-muted);
}
:host .ws-rich-check {
  color: var(--ws-rich-accent);
  flex-shrink: 0;
  margin-top: 2px;
  display: inline-flex;
}
:host .ws-rich-membership-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
}

/* ── Event card — cover bg + blurred overlay. ────────────────────────── */
:host .ws-rich-event {
  position: relative;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  min-height: 220px;
  border-radius: 8px;
  overflow: hidden;
  color: #FFFFFF;
}
:host .ws-rich-event-inner {
  position: absolute;
  inset: auto 0 0 0;
  padding: 10px 12px;
  background: var(--ws-rich-event-overlay);
  -webkit-backdrop-filter: blur(30px);
  backdrop-filter: blur(30px);
}
:host .ws-rich-event .ws-card-title { color: #FFFFFF; }
:host .ws-rich-event .ws-card-meta,
:host .ws-rich-event .ws-card-desc {
  color: rgba(255,255,255,0.85);
}


/* ── Event details — strip + price + reserve. ────────────────────────── */
:host .ws-rich-event-details {
  background: #F6F6F6;
  border-radius: 8px;
  overflow: hidden;
}
:host .ws-rich-event-strip {
  height: 18px;
  background: linear-gradient(90deg, var(--ws-rich-deep) 0%, var(--ws-rich-accent) 50%, var(--ws-rich-deep) 100%);
}

:host .ws-event-details-card .ws-card-meta-secondary {
  color: #000;
  font-weight: 600;
}

:host .ws-rich-event-date {
  color: var(--ws-rich-accent);
  font-weight: 600;
  margin: 8px 0 0;
}
:host .ws-rich-event-price {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--ws-rich-accent);
}
:host .ws-rich-reserve-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--ws-rich-deep);
  color: var(--ws-rich-on-accent);
}

/* ── Reviews button row. Common look for every tenant (old .review-button:
   white pill, brown border+text, rounded 8px, platform icon) — sized to its
   own content, not stretched full-width. ─────────────────────────────── */
:host .ws-rich-reviews {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
:host .ws-rich-review-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #574d39;
  border-radius: 8px;
  color: #574d39;
  text-decoration: none;
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
}

:host .ws-rich-review-icon {
  display: inline-flex;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}
:host .ws-rich-review-icon svg {
  display: block;
  width: 100%;
  height: 100%;
}
/* Review items render through the generic card carousel (cardType "review"),
   but a horizontally-scrolling row of tiny link buttons reads poorly for a
   short, unordered list — lay it out as a vertical stack with no nav arrows,
   buttons sized to their own content (align-items: flex-start keeps a column
   flex container from stretching children to fill the row width). */
:host .ws-carousel:has(.ws-rich-review-button) .ws-carousel-track {
  flex-direction: column;
  align-items: flex-start;
  overflow-x: visible;
  gap: 8px;
}
:host .ws-carousel:has(.ws-rich-review-button) .ws-carousel-nav {
  display: none;
}

/* ── Cart panel. ─────────────────────────────────────────────── */

:host .ws-cart-product-details {
  display: flex;
  justify-content: space-between;
  align-items:center;
}

:host .cart-quantity-wrapper {
  gap: 10px !important;
}